// src/utils/gameState.js

/**
 * Validates the structure of the game state.
 * @param {Object} state - The game state to validate.
 * @returns {boolean} - Returns true if valid, false otherwise.
 */
const isValidGameState = (state) => {
  if (typeof state !== 'object' || state === null) return false;

  const requiredFields = ['day', 'money', 'rating', 'stats', 'dayComplete', 'trainingMode'];
  for (const field of requiredFields) {
    if (!(field in state)) return false;
  }

  if (typeof state.day !== 'number' || state.day < 1 || state.day > 20) return false;
  if (typeof state.money !== 'number' || state.money < 0 || state.money > 5000) return false;
  if (typeof state.rating !== 'number' || state.rating < 0 || state.rating > 50) return false;
  if (typeof state.stats !== 'object' || state.stats === null) return false;
  if (typeof state.dayComplete !== 'boolean') return false;
  if (typeof state.trainingMode !== 'boolean') return false;

  // Further validation for nested stats
  const { totalRevenue, totalCustomers } = state.stats;
  if (typeof totalRevenue !== 'number' || totalRevenue < 0 || totalRevenue > 20000) return false;
  if (typeof totalCustomers !== 'number' || totalCustomers < 0 || totalCustomers > 2000) return false;

  return true;
};

/**
 * Updates the game state in localStorage
 * @param {Object} updates - The updates to apply to the game state.
 */
export const updateGameState = (updates) => {
  const savedGame = loadGameState() || {};
  const newGameState = { ...savedGame, ...updates };
  localStorage.setItem('gameState', JSON.stringify(newGameState));
};

/**
 * Loads the game state from localStorage with validation.
 * @returns {Object|null} - Returns the valid game state or null if invalid.
 */
export const loadGameState = () => {
  const saved = localStorage.getItem('gameState');
  if (!saved) return null;

  try {
    const parsed = JSON.parse(saved);
    return isValidGameState(parsed) ? parsed : null;
  } catch (error) {
    console.error('Error parsing game state:', error);
    return null;
  }
};

/**
 * Clears the game state from localStorage
 */
export const clearGameState = () => {
  localStorage.removeItem('gameState');
};